import type {
  DoorConfig,
  HouseBodyConfig,
  PartitionDoorConfig,
  RoomConfig,
  SlabHoleConfig,
  StairConfig,
} from '@/model/schema';
import type { Rng } from './rng';

/**
 * Picks one stair flight per inter-story junction.
 *
 * v3 invariants (after the user-reported issues with stairs ending in front
 * of doors and crossing partition walls on the upper floor):
 *
 *  1. The stair footprint is fully inside ONE lower-story room.
 *  2. The stair footprint is fully inside ONE upper-story room (otherwise
 *     a partition wall would slice through the stair well).
 *  3. The footprint plus its top landing must not overlap any door swing
 *     zone — exterior doors on either story, or interior partition doors.
 *  4. The uphill end keeps `LANDING_CLEARANCE` away from the upper room's
 *     far wall so you actually step off onto floor.
 *
 * Visualised in @ src/render/builders/buildStairs.ts.
 */
export interface StairsResult {
  stairs: StairConfig[];
  /** Slab holes per story level — keyed by storyLevel. */
  slabHolesByStory: Map<number, SlabHoleConfig[]>;
}

export interface StoryPlanData {
  rooms: RoomConfig[];
  doors: DoorConfig[];
  partitionDoors: PartitionDoorConfig[];
}

/**
 * Find a stair-sized slot inside one of `story`'s rooms — same room
 * ordering and forbidden-zone logic as the inter-story planner, but
 * used by callers that only need a single-story slot (e.g. the
 * basement-access stair whose upper landing IS on L0). Returns the
 * chosen slot's X/Z/width/depth/climbDir in body-local coords, or
 * `null` if no room in `story` can host a stair.
 *
 * `fallbackClimbDir` is used when we have to squeeze into a tight
 * room and the enumerator can't pick a direction — the caller can
 * hint a preferred axis so the basement stair lands facing into the
 * larger part of the room.
 */
export function findStairSlotInStory(
  rng: Rng,
  body: HouseBodyConfig,
  story: StoryPlanData,
  extraForbidden: Rect[] = [],
): Slot | null {
  const forbidden = [...collectForbiddenRects(body, story), ...extraForbidden];
  const orderedRooms = orderHostRooms(rng.fork('host'), story.rooms);
  for (const room of orderedRooms) {
    const slots = enumerateSlots(room, body);
    for (const slot of slots) {
      const containing = roomContaining(story.rooms, slot);
      if (!containing) continue;
      if (overlapsAny(slot, forbidden)) continue;
      if (overlapsAny(landingRect(slot), forbidden)) continue;
      return slot;
    }
  }
  return null;
}

const STAIR_WIDTH = 1.0;
const STAIR_DEPTH = 2.6;
const WALL_CLEARANCE = 0.25;
const LANDING_CLEARANCE = 0.6;
/** Inward depth of the "approach zone" reserved in front of an exterior
 *  door — a stair sitting in here would block the entry. */
const EXT_DOOR_APPROACH = 1.2;
/** Half-thickness of the forbidden band around an interior doorway. The
 *  band straddles both rooms the doorway connects so neither side can
 *  put a stair right at the door's mouth. */
const PARTITION_DOOR_PAD = 0.45;
/** Extra clearance added to door rectangles before checking against the
 *  stair footprint, so a stair can't end up tangent to a doorway. */
const DOOR_RECT_PAD = 0.1;

export interface Rect {
  xLo: number;
  xHi: number;
  zLo: number;
  zHi: number;
}

export interface Slot {
  x: number;
  z: number;
  width: number;
  depth: number;
  climbDir: StairConfig['climbDir'];
}

export function planStairs(
  rng: Rng,
  body: HouseBodyConfig,
  storyData: StoryPlanData[],
): StairsResult {
  const out: StairsResult = { stairs: [], slabHolesByStory: new Map() };

  for (let upper = 1; upper < storyData.length; upper++) {
    const lower = storyData[upper - 1]!;
    const upperData = storyData[upper]!;
    if (lower.rooms.length === 0 || upperData.rooms.length === 0) continue;

    const lowerForbidden = collectForbiddenRects(body, lower);
    const upperForbidden = collectForbiddenRects(body, upperData);

    const slot = chooseSlot(
      rng.fork(`stair-${upper}`),
      body,
      lower.rooms,
      upperData.rooms,
      lowerForbidden,
      upperForbidden,
    );
    if (!slot) continue;

    out.stairs.push({
      toLevel: upper,
      x: slot.x,
      z: slot.z,
      width: slot.width,
      depth: slot.depth,
      climbDir: slot.climbDir,
    });

    const list = out.slabHolesByStory.get(upper) ?? [];
    list.push({ x: slot.x, z: slot.z, width: slot.width, depth: slot.depth });
    out.slabHolesByStory.set(upper, list);
  }
  return out;
}

/**
 * Score every (lowerRoom × upperRoom × slot) triple and return the best
 * one that satisfies all hard constraints. Falls back to a centred placement
 * inside the largest plausible lower room if nothing passes — never returns
 * null for a multi-story house.
 */
function chooseSlot(
  rng: Rng,
  body: HouseBodyConfig,
  lowerRooms: RoomConfig[],
  upperRooms: RoomConfig[],
  lowerForbidden: Rect[],
  upperForbidden: Rect[],
): Slot | null {
  const orderedLower = orderHostRooms(rng.fork('host-lower'), lowerRooms);

  for (const lowerRoom of orderedLower) {
    const slots = enumerateSlots(lowerRoom, body);
    for (const slot of slots) {
      const upperRoom = roomContaining(upperRooms, slot);
      if (!upperRoom) continue;
      if (!hasLandingClearance(slot, upperRoom)) continue;
      if (overlapsAny(slot, lowerForbidden)) continue;
      if (overlapsAny(slot, upperForbidden)) continue;
      // Also verify the landing-side approach (one tread depth past the
      // top of the flight) is clear of door zones.
      const landing = landingRect(slot);
      if (overlapsAny(landing, upperForbidden)) continue;
      return slot;
    }
  }

  // Fallback: take the largest lower room and squeeze a stair in even if
  // it crosses a partition. This keeps a multi-story house usable in
  // pathological layouts; the renderer will still draw treads.
  const fallbackRoom = orderedLower[0];
  if (!fallbackRoom) return null;
  const fallbackSlots = enumerateSlots(fallbackRoom, body);
  return fallbackSlots[0] ?? null;
}

function orderHostRooms(rng: Rng, rooms: RoomConfig[]): RoomConfig[] {
  // Prefer halls; otherwise sort by area descending. Within each tier,
  // shuffle a tiny bit so reseeds vary.
  const halls = rooms.filter((r) => r.type === 'hall');
  const others = rooms.filter((r) => r.type !== 'hall');
  const byArea = (a: RoomConfig, b: RoomConfig) => b.width * b.depth - a.width * a.depth;
  halls.sort(byArea);
  others.sort(byArea);
  // Light shuffle inside the top-3 of each tier so seed permutations matter.
  shuffleTop(rng, halls, 3);
  shuffleTop(rng, others, 3);
  return [...halls, ...others];
}

function shuffleTop(rng: Rng, arr: RoomConfig[], k: number): void {
  const n = Math.min(k, arr.length);
  for (let i = 0; i < n - 1; i++) {
    const j = i + Math.floor(rng.next() * (n - i));
    if (j !== i) {
      const tmp = arr[i]!;
      arr[i] = arr[j]!;
      arr[j] = tmp;
    }
  }
}

/**
 * Enumerate every plausible slot inside `room`: both orientations, both
 * climb directions, and the lateral edges of the room. The slot's uphill
 * end is biased toward the room's interior so the landing has space.
 *
 * Slots are returned in a deterministic order (no RNG inside this fn) so
 * the validity loop in `chooseSlot` always picks the same first-passing
 * one for a given seed. The seed only affects room ordering above.
 */
function enumerateSlots(room: RoomConfig, body: HouseBodyConfig): Slot[] {
  const out: Slot[] = [];
  const orientations: Array<{ w: number; d: number; portrait: boolean }> = [
    { w: STAIR_WIDTH, d: STAIR_DEPTH, portrait: true },
    { w: STAIR_DEPTH, d: STAIR_WIDTH, portrait: false },
  ];

  for (const o of orientations) {
    if (room.width < o.w + 0.2 || room.depth < o.d + 0.2) continue;
    out.push(...candidatesForOrientation(room, body, o.w, o.d, o.portrait));
  }

  // Last resort: shrink to fit and retry if the room is too tight in both
  // orientations. We still tag the climb direction so the renderer keeps
  // its landing logic.
  if (out.length === 0) {
    const w = Math.max(0.7, Math.min(STAIR_WIDTH, room.width - 0.1));
    const d = Math.max(1.5, Math.min(STAIR_DEPTH, room.depth - 0.1));
    const portrait = d >= w;
    out.push(...candidatesForOrientation(room, body, w, d, portrait));
  }

  return out;
}

function candidatesForOrientation(
  room: RoomConfig,
  body: HouseBodyConfig,
  w: number,
  d: number,
  portrait: boolean,
): Slot[] {
  const out: Slot[] = [];

  // Keep WALL_CLEARANCE away from BOTH the exterior wall and the room's
  // own partitions. Without the room.x + WALL_CLEARANCE term the stair
  // can sit flush against an interior partition and read like the user-
  // reported image of a stair touching a wall.
  const lateralXLo = Math.max(room.x + WALL_CLEARANCE, WALL_CLEARANCE);
  const lateralXHi = Math.min(
    room.x + room.width - w - WALL_CLEARANCE,
    body.footprintWidth - WALL_CLEARANCE - w,
  );
  const lateralZLo = Math.max(room.z + WALL_CLEARANCE, WALL_CLEARANCE);
  const lateralZHi = Math.min(
    room.z + room.depth - d - WALL_CLEARANCE,
    body.footprintDepth - WALL_CLEARANCE - d,
  );

  if (portrait) {
    // Climb along Z. BOTH ends need LANDING_CLEARANCE from the room's
    // Z walls so you can stand in front of the first tread AND step off
    // onto the upper floor without bumping your nose into a partition.
    // Previously we only enforced the uphill landing; users reported the
    // lower tread consistently sitting hard against a wall.
    const zPlusMin = Math.max(lateralZLo, room.z + LANDING_CLEARANCE);
    const zPlusMax = Math.min(
      lateralZHi,
      room.z + room.depth - d - LANDING_CLEARANCE,
    );
    const zMinusMin = Math.max(lateralZLo, room.z + LANDING_CLEARANCE);
    const zMinusMax = Math.min(
      lateralZHi,
      room.z + room.depth - d - LANDING_CLEARANCE,
    );
    const xLeft = Math.min(lateralXLo, lateralXHi);
    const xRight = Math.max(lateralXLo, lateralXHi);

    if (zPlusMin <= zPlusMax) {
      out.push({ x: xLeft, z: zPlusMin, width: w, depth: d, climbDir: 'plusZ' });
      if (xRight !== xLeft) {
        out.push({ x: xRight, z: zPlusMin, width: w, depth: d, climbDir: 'plusZ' });
      }
    }
    if (zMinusMin <= zMinusMax) {
      out.push({ x: xLeft, z: zMinusMax, width: w, depth: d, climbDir: 'minusZ' });
      if (xRight !== xLeft) {
        out.push({ x: xRight, z: zMinusMax, width: w, depth: d, climbDir: 'minusZ' });
      }
    }
  } else {
    // Climb along X — same rationale as portrait branch above, both ends.
    const xPlusMin = Math.max(lateralXLo, room.x + LANDING_CLEARANCE);
    const xPlusMax = Math.min(
      lateralXHi,
      room.x + room.width - w - LANDING_CLEARANCE,
    );
    const xMinusMin = Math.max(lateralXLo, room.x + LANDING_CLEARANCE);
    const xMinusMax = Math.min(
      lateralXHi,
      room.x + room.width - w - LANDING_CLEARANCE,
    );
    const zFront = Math.min(lateralZLo, lateralZHi);
    const zBack = Math.max(lateralZLo, lateralZHi);

    if (xPlusMin <= xPlusMax) {
      out.push({ x: xPlusMin, z: zFront, width: w, depth: d, climbDir: 'plusX' });
      if (zBack !== zFront) {
        out.push({ x: xPlusMin, z: zBack, width: w, depth: d, climbDir: 'plusX' });
      }
    }
    if (xMinusMin <= xMinusMax) {
      out.push({ x: xMinusMax, z: zFront, width: w, depth: d, climbDir: 'minusX' });
      if (zBack !== zFront) {
        out.push({ x: xMinusMax, z: zBack, width: w, depth: d, climbDir: 'minusX' });
      }
    }
  }

  return out;
}

/** True when the slot rectangle is fully contained inside `room`. */
function roomContaining(rooms: RoomConfig[], slot: Slot): RoomConfig | null {
  const eps = 0.01;
  for (const r of rooms) {
    if (
      slot.x >= r.x - eps &&
      slot.z >= r.z - eps &&
      slot.x + slot.width <= r.x + r.width + eps &&
      slot.z + slot.depth <= r.z + r.depth + eps
    ) {
      return r;
    }
  }
  return null;
}

/** True when the upper room extends at least LANDING_CLEARANCE beyond the
 *  uphill end of the slot, so there's somewhere to step off onto. */
function hasLandingClearance(slot: Slot, upperRoom: RoomConfig): boolean {
  switch (slot.climbDir) {
    case 'plusZ':
      return upperRoom.z + upperRoom.depth - (slot.z + slot.depth) >= LANDING_CLEARANCE - 0.01;
    case 'minusZ':
      return slot.z - upperRoom.z >= LANDING_CLEARANCE - 0.01;
    case 'plusX':
      return upperRoom.x + upperRoom.width - (slot.x + slot.width) >= LANDING_CLEARANCE - 0.01;
    case 'minusX':
      return slot.x - upperRoom.x >= LANDING_CLEARANCE - 0.01;
  }
}

/** Build a small rectangle representing the floor area immediately past the
 *  uphill tread (where you actually set foot down on the upper story). We
 *  test this against upper-story door zones to keep landings clear. */
function landingRect(slot: Slot): Rect {
  const lateral = slot.climbDir === 'plusX' || slot.climbDir === 'minusX' ? slot.depth : slot.width;
  const reach = LANDING_CLEARANCE;
  switch (slot.climbDir) {
    case 'plusZ':
      return { xLo: slot.x, xHi: slot.x + lateral, zLo: slot.z + slot.depth, zHi: slot.z + slot.depth + reach };
    case 'minusZ':
      return { xLo: slot.x, xHi: slot.x + lateral, zLo: slot.z - reach, zHi: slot.z };
    case 'plusX':
      return { xLo: slot.x + slot.width, xHi: slot.x + slot.width + reach, zLo: slot.z, zHi: slot.z + lateral };
    case 'minusX':
      return { xLo: slot.x - reach, xHi: slot.x, zLo: slot.z, zHi: slot.z + lateral };
  }
}

/* ------------------------- forbidden-zone math ------------------------ */

function collectForbiddenRects(body: HouseBodyConfig, story: StoryPlanData): Rect[] {
  const out: Rect[] = [];
  for (const door of story.doors) {
    out.push(exteriorDoorRect(body, door));
  }
  for (const door of story.partitionDoors) {
    const rect = partitionDoorRect(story.rooms, door);
    if (rect) out.push(rect);
  }
  return out;
}

function exteriorDoorRect(body: HouseBodyConfig, door: DoorConfig): Rect {
  const inside = EXT_DOOR_APPROACH;
  const pad = DOOR_RECT_PAD;
  switch (door.side) {
    case 'front':
      return {
        xLo: door.offset - pad,
        xHi: door.offset + door.width + pad,
        zLo: 0,
        zHi: inside,
      };
    case 'back':
      return {
        xLo: body.footprintWidth - door.offset - door.width - pad,
        xHi: body.footprintWidth - door.offset + pad,
        zLo: body.footprintDepth - inside,
        zHi: body.footprintDepth,
      };
    case 'left':
      return {
        xLo: 0,
        xHi: inside,
        zLo: body.footprintDepth - door.offset - door.width - pad,
        zHi: body.footprintDepth - door.offset + pad,
      };
    case 'right':
      return {
        xLo: body.footprintWidth - inside,
        xHi: body.footprintWidth,
        zLo: door.offset - pad,
        zHi: door.offset + door.width + pad,
      };
  }
}

function partitionDoorRect(rooms: RoomConfig[], door: PartitionDoorConfig): Rect | null {
  const owner = rooms.find((r) => r.id === door.ownerRoomId);
  if (!owner) return null;
  const halfW = door.width / 2 + DOOR_RECT_PAD;
  if (door.axis === 'X') {
    // Vertical wall along Z at x = owner.x + owner.width.
    const wallX = owner.x + owner.width;
    const cz = owner.z + door.tCenter * owner.depth;
    return {
      xLo: wallX - PARTITION_DOOR_PAD,
      xHi: wallX + PARTITION_DOOR_PAD,
      zLo: cz - halfW,
      zHi: cz + halfW,
    };
  }
  // 'Z' axis: horizontal wall along X at z = owner.z + owner.depth.
  const wallZ = owner.z + owner.depth;
  const cx = owner.x + door.tCenter * owner.width;
  return {
    xLo: cx - halfW,
    xHi: cx + halfW,
    zLo: wallZ - PARTITION_DOOR_PAD,
    zHi: wallZ + PARTITION_DOOR_PAD,
  };
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.xLo < b.xHi && a.xHi > b.xLo && a.zLo < b.zHi && a.zHi > b.zLo;
}

function overlapsAny(slotOrRect: Slot | Rect, rects: Rect[]): boolean {
  const r: Rect =
    'width' in slotOrRect
      ? {
          xLo: slotOrRect.x,
          xHi: slotOrRect.x + slotOrRect.width,
          zLo: slotOrRect.z,
          zHi: slotOrRect.z + slotOrRect.depth,
        }
      : slotOrRect;
  for (const f of rects) {
    if (rectsOverlap(r, f)) return true;
  }
  return false;
}
